// NPM
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { isNil, cloneDeep, map, omitBy, isEmpty } from 'lodash';

// COMPONENTS
import ProductPurchaseInfo from 'Components/order/purchase-list';
import PaymentList from 'Components/order/payment-list';

// ATOM
import { orderAtom } from 'Recoil/order.atom';
import { paymentStatusAtom } from "Recoil/payment.atom";

// SERVICE
import { fetchOrderList, cancelOrder, downloadReceipt, fetchPaymentList } from "Services/order.services";
import { fetchQuotationRequestList } from "Services/quotation-request.services";
import { fetchPaymentStatus } from "Services/payment.services";
import { goodsDispatchOrderClose } from 'Services/goods.services';

// HELPER
import { toaster, encode } from 'Helpers/index';

// HOOK
import useSession from "Hooks/session.hook";
import { useItemsPerPage, useQuery } from 'Components/shared/hooks'

// ANT COMPONENT
import { Button, ActionMenu, Pagination, Tabs } from "@life-line-lab/components-react";
import Icon, { PlusOutlined, DatabaseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

// CONSTANTS
import { DEFAULT_MAX_PER_PAGE } from 'Components/shared/constants/config';

// STYLE
import "./purchase-list.scss"
import PrescriptionList from 'Components/order/prescription-list';
import { ItemType } from '../../../models/quotation';

interface ProductPurchasePageProps {

}


const ProductPurchasePage: React.FC<ProductPurchasePageProps> = () => {
  const query = useQuery();
  const history = useHistory();
  const { t } = useTranslation();

  const [orderState, setOrderState] = useRecoilState<any>(orderAtom);
  const [paymentStatusState, setPaymentStatusState] = useRecoilState<any>(paymentStatusAtom);
  const [itemPerPage, setItemPerPage] = useItemsPerPage(DEFAULT_MAX_PER_PAGE.toString());
  const [allOrderList, setAllOrderList] = useState<any>([]);
  const [selectedOrderID, setSelectedOrderID] = useState<any>();

  const page: any = query.get('page') || 1;
  const initialQueryParams = { page, offset: (page - 1) * DEFAULT_MAX_PER_PAGE }
  const [queryParams, setQueryParams] = useState(initialQueryParams);
  const [tabActiveKey, setTabActiveKey] = useState("0");


  const { profile } = useSession();

  useEffect(() => {
    const session = useSession();
    if (isEmpty(session)) {
        history.push('/login');
        return;
    }
  }, [])

  useEffect(() => {
    // fetchOrders()
    handleTabClick(tabActiveKey, '')
  }, [queryParams.offset])


  const fetchOrders = async (status?,withVoucher?) => {
    let profileIDValue = profile._id

    setOrderState({ ...orderState, status: { ...orderState.status, fetch: 'loading' } });

    let filters: any = { orderedBy: profileIDValue, orderType: ['ECOM_RETAIL'] }
    if (status) {
      filters.status = status;
      // filters.paymentStatus = 'PAID';
    }
    filters.withVoucher = withVoucher;
    const responseData = await fetchOrderList(omitBy(filters,isNil), { offset: queryParams.offset, limit: itemPerPage }, { sortBy: 'orderCode', mode: "DESC" });


    if (!isNil(responseData)) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: 'success' },
        list: responseData
      });

      setAllOrderList(responseData)
    }
    else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: 'error' }
      });
    }
  }

  /**
   * Fetch a list of payments with each payment can have multiple orders (1 seller == 1 order)  
   * 
   */
  const fetchToPayOrders = async () => {
    let profileIDValue = profile._id

    setOrderState({ ...orderState, status: { ...orderState.status, fetch: 'loading' } });

    let filters: any = { 
      payer: profileIDValue,
      status: ['ABORTED', 'FAILED', 'PENDING'],
      orderStatus: ['PENDING_PAYMENT'],
      orderType: ['ECOM_RETAIL']
    }

    const responseData = await fetchPaymentList(omitBy(filters,isNil), { offset: queryParams.offset, limit: itemPerPage }, { sortBy: 'updatedAt', mode: "DESC" });

    if (!isNil(responseData)) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: 'success' },
        list: responseData
      });

      setAllOrderList(responseData);
    }
    else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: 'error' }
      });
    }
  }

  const fetchPrescriptions = async () => {
    let profileIDValue = profile._id

    setOrderState({ ...orderState, status: { ...orderState.status, fetch: 'loading' } });

    let filters: any = { 
      requestedBy: profileIDValue,
      // requestTypes: ['PRESCRIPTION'],
      sourceSite: process.env.ACCOUNT_TYPE,
    }

    const responseData = await fetchQuotationRequestList(omitBy(filters,isNil), { offset: queryParams.offset, limit: itemPerPage }, { sortBy: 'createdAt', mode: "DESC" });

    if (!isNil(responseData)) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: 'success' },
        list: responseData
      });

      setAllOrderList(responseData);
    }
    else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: 'error' }
      });
    }
  }

  const handleCancelOrder = async (cancelObj) => {

    setOrderState({ ...orderState, status: { ...orderState.status, cancel: 'loading' } });
    const responseData = await cancelOrder(cancelObj);
    if (!isNil(responseData) && responseData.updated) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, cancel: 'success' },
        list: responseData
      });
      toaster('success', t("toaster.orderCancelledSuccessfully"));

      //fetchOrders()
      handleTabClick(tabActiveKey, '')
    }
    else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, cancel: 'error' }
      });
      toaster('error', t("toaster.failedCancelOrder"));
    }
  }

  const handleReceipt = async (obj) => {

    setOrderState({ ...orderState, status: { ...orderState.status, receipt: 'loading' } });

    const responseData = await downloadReceipt(obj);

    if (!isNil(responseData) && !isNil(responseData.receipt)) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, receipt: 'success', data: responseData.receipt },
      });
    }
    else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, receipt: 'failed' }
      });
      toaster('error', t("toaster.failedToGetReceipt"));
    }

  }

  const handlePaymentStatus = async (obj) => {

    setPaymentStatusState({ ...paymentStatusState, fetch: 'loading' });

    const responseData = await fetchPaymentStatus({ paymentID: obj.paymentID });

    if (!isNil(responseData) && !isNil(responseData.paymentStatus)) {
      let orderList = map(cloneDeep(allOrderList), orderObj => {
        if (orderObj._id == obj.orderID) {
          orderObj.payment_data.status = responseData.paymentStatus;
        }
        return orderObj;
      })
      setAllOrderList(orderList)
      setPaymentStatusState({
        ...paymentStatusState, fetch: 'success'
      });
    }
    else {
      setPaymentStatusState({
        ...paymentStatusState, fetch: 'failed'
      });
      toaster('error', t("toaster.failedToGetStatus"));
    }

  }

  const handleItemsPerPageChange = (value: string) => {
    // setItemPerPage(value)
  }

  const handlePaginationChange = (e: string) => {
    let page = queryParams.page;
    let limit: any = itemPerPage;

    if (e === 'NEXT') {
      if (allOrderList.length == itemPerPage) ++page;
      if (page > itemPerPage) page = itemPerPage;
    }

    if (e === 'PREVIOUS') {
      --page;
      if (page < 1) page = 1;
    }

    const offset = (page - 1) * limit;
    setQueryParams({ ...queryParams, page, offset });

    // if (offset > 0)
    //   history.push({ pathname: '/suppliers/inventory', search: `?page=${page}` });
    // else
    //   history.push('/suppliers/inventory');
  }

  const closeOrder = async (data) => {
    const responseData: any = await goodsDispatchOrderClose(data);
    if (!isNil(responseData) && !isNil(responseData.closed)) {
      handleTabClick(tabActiveKey, '');
      toaster('success', t('toaster.closeOrderSuccess'));
    } else {
      toaster('error', t('toaster.closeOrderFail'));
    }
  }

  const handleTabClick = (key, e) => {
    switch (key) {
      case "7":
        //VOUCHER TAB
        fetchPrescriptions();
        break;

      case "6":
        //VOUCHER TAB
        fetchOrders(null,true);
        break;

      case "5":
        //CANCELLED TAB
        fetchOrders(['CANCELLED','REJECTED','REFUNDED']);
        break;

      case "4":
        //CLOSED TAB
        fetchOrders(['CLOSED']);
        break;

      case "3":
        //TO RECEIVE
        fetchOrders(['DISPATCH_INPROGRESS', 'DELIVERED'],false);
        break;

      case "2":
        //TO SHIP
        fetchOrders(['OPEN','APPROVED'],false);
        break;

      case "1":
        //TO PAY TAB
        fetchToPayOrders();
        break;

      case "0":
        //ALL
        fetchOrders();
        break;

      default:
        break;
    }
    setTabActiveKey(key);
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>
          My Purchase
        </title>
      </Helmet>
      <div className="page purchase-list-page" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        <div style={{ position: 'relative', background: '#ffffff', borderRadius: '4px', padding: '20px' }}>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <h3 className="title" style={{ color: '#CF4392', fontSize: '24px' }}>{t('title.myPurchase')}</h3>
          </div>

          <Tabs
            onTabClick={handleTabClick}
            activeKey={tabActiveKey}
            tabBarGutter={20}
            tabs={[
              {
                label: t("tab.all"),
                component: (
                  <div>
                    <ProductPurchaseInfo
                      ordersList={allOrderList}
                      status={orderState.status}
                      cancelOrder={handleCancelOrder}
                      getReceipt={handleReceipt}
                      getPaymentStatus={handlePaymentStatus}
                      paymentStatus={paymentStatusState.fetch}
                      handleCloseOrder={closeOrder}
                    />
                    {allOrderList.length !== 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }
                  </div>
                ),
              },
              {
                label: t('tab.toPay'),
                component: (
                  <div>
                    <PaymentList
                      status={orderState.status}
                      paymentsDataList={allOrderList}
                      cancelOrder={handleCancelOrder}
                    />
                    {allOrderList.length > 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
              {
                label: t("tab.toShip"),
                component: (
                  <div>
                    <ProductPurchaseInfo
                      ordersList={allOrderList}
                      status={orderState.status}
                      cancelOrder={handleCancelOrder}
                      getReceipt={handleReceipt}
                      getPaymentStatus={handlePaymentStatus}
                      paymentStatus={paymentStatusState.fetch}
                      handleCloseOrder={closeOrder}
                    />
                    {allOrderList.length !== 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
              {
                label: t("tab.toReceive"),
                component: (
                  <div>
                    <ProductPurchaseInfo
                      ordersList={allOrderList}
                      status={orderState.status}
                      cancelOrder={handleCancelOrder}
                      getReceipt={handleReceipt}
                      getPaymentStatus={handlePaymentStatus}
                      paymentStatus={paymentStatusState.fetch}
                      handleCloseOrder={closeOrder}
                    />
                    {allOrderList.length !== 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
              {
                label: t("tab.completed"),
                component: (
                  <div>
                    <ProductPurchaseInfo
                      ordersList={allOrderList}
                      status={orderState.status}
                      cancelOrder={handleCancelOrder}
                      getReceipt={handleReceipt}
                      getPaymentStatus={handlePaymentStatus}
                      paymentStatus={paymentStatusState.fetch}
                      handleCloseOrder={closeOrder}
                    />
                    {allOrderList.length !== 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
              {
                label: t("tab.cancelled"),
                component: (
                  <div>
                    <ProductPurchaseInfo
                      ordersList={allOrderList}
                      status={orderState.status}
                      cancelOrder={handleCancelOrder}
                      getReceipt={handleReceipt}
                      getPaymentStatus={handlePaymentStatus}
                      paymentStatus={paymentStatusState.fetch}
                      handleCloseOrder={closeOrder}
                    />
                    {allOrderList.length !== 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
              {
                label: t("tab.vouchers"),
                component: (
                  <div>
                    <ProductPurchaseInfo
                      ordersList={allOrderList}
                      status={orderState.status}
                      cancelOrder={handleCancelOrder}
                      getReceipt={handleReceipt}
                      getPaymentStatus={handlePaymentStatus}
                      paymentStatus={paymentStatusState.fetch}
                      handleCloseOrder={closeOrder}
                    />
                    {allOrderList.length !== 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
              {
                label: t('tab.prescriptions'),
                component: (
                  <div>
                    <PrescriptionList
                      status={orderState.status}
                      prescriptionDataList={allOrderList}
                    />
                    {allOrderList.length > 0 &&
                      <div className="my-30">
                        <Pagination
                          previousIcon={<span><LeftOutlined />{t('action.previous')} </span>}
                          nextIcon={<span><RightOutlined />{t('action.next')}</span>}
                          // itemsPerPageValue={itemPerPage}
                          itemsPerPageConfig={{
                            initialValue: DEFAULT_MAX_PER_PAGE,
                            prefixLabel: t('label.show'),
                            suffixLabel: t('txt.records')
                          }}
                          // onItemsPerPageChange={handleItemsPerPageChange}
                          onChange={handlePaginationChange}
                        />
                      </div>
                    }

                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </React.Fragment >
  )
}

export default ProductPurchasePage;
